import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { FC } from 'react';
import { Enumeration } from 'src/common/types';
import { DateRange } from 'src/store';
import { AssetAllocationProps } from '../components/report/AssetAllocation';
import { AssetClassPerformanceProps } from '../components/report/AssetClassPerformance';
import { CashTransactionsProps } from '../components/report/CashTransactions';
import { ContributionsAndWithdrawalsProps } from '../components/report/ContributionsAndWithdrawals';
import { CurrencyExposureProps } from '../components/report/CurrencyExposure';
import { ExpensesProps } from '../components/report/Expenses';
import { HoldingsVsTargetVarianceProps } from '../components/report/HoldingsVsTargetVariance';
import { IncomeReportProps } from '../components/report/IncomeReport';
import { InvestmentIncomeTaxProps } from '../components/report/InvestmentIncomeTax';
import { PorfolioPerformanceProps } from '../components/report/PortfolioPerformance';
import { PortfolioSnapshotProps } from '../components/report/PortfolioSnapshot';
import { PortfolioValuationProps } from '../components/report/PortfolioValuation';
import { ProjectedIncomeCashflowProps } from '../components/report/ProjectedIncomeCashflow';
import { RealisedCapitalGainsAndLossesProps } from '../components/report/RealisedCapitalGainsAndLosses';
import { SecurityContributionProps } from '../components/report/SecurityContribution';
import { SecurityPerformanceProps } from '../components/report/SecurityPerformance';
import { TaxInvoiceProps } from '../components/report/TaxInvoice';
import { TaxSummaryProps } from '../components/report/TaxSummary';
import { TransactionHistoryProps } from '../components/report/TransactionHistory';
import { UnrealisedCapitalGainsAndLossesProps } from '../components/report/UnrealisedCapitalGainsAndLosses';
import { UnrealisedNonCGTGainsAndLossesProps } from '../components/report/UnrealisedNonCGTGainsAndLosses';
import { UXChangeTaxStatementReportProps } from '../components/report/UXChangeTaxStatementReport';

export interface ClientNewReportsState {
  parameters: ClientReportsParameters;
  reportResult?: ReportResult | null;
  reportGeneration: ReportGeneration;
  savedParametersList: SavedParameter[];
  clientFinancialYears: CGTRecord[];
}

export enum MdaReportTypeField {
  ContributionsAndWithdrawals = 'contributionsAndWithdrawals',
  AssetAllocation = 'assetAllocation',
  AssetClassPerformance = 'assetClassPerformance',
  CashTransactions = 'cashTransactions',
  Expenses = 'expenses',
  IncomeReport = 'incomeReport',
  PortfolioPerformance = 'portfolioPerformance',
  PortfolioSnapshot = 'portfolioSnapshot',
  ProjectedIncomeCashflow = 'projectedIncomeCashflow',
  PortfolioValuation = 'portfolioValuation',
  SecurityContribution = 'securityContribution',
  SecurityPerformance = 'securityPerformance',
  TransactionHistory = 'transactionHistory',
  CurrencyExposure = 'currencyExposure',
}

export enum MdaReportType {
  PortfolioValuation = 'Portfolio Valuation',
  CashTransactions = 'Cash Transactions',
  IncomeReport = 'Income Report',
  PortfolioSnapshot = 'Portfolio Snapshot',
  PortfolioPerformance = 'Portfolio Performance',
  SecurityContribution = 'Security Contribution',
  TransactionHistory = 'Transaction History',
  TaxSummary = 'Tax Summary',
  TaxInvoice = 'Tax Invoice',
  UnrealisedNonCGT = 'Unrealised Non CGT',
  RealisedCapitalGainsAndLosses = 'Realised Capital Gains & Losses',
  UnrealisedCapitalGainsAndLosses = 'Unrealised Capital Gains & Losses',
  ContributionsAndWithdrawals = 'Contributions & Withdrawals',
  AssetClassPerformance = 'Asset Class Performance',
  HoldingsVsTargetVariance = 'Holdings vs Target Variance',
  Expenses = 'Expenses',
  SecurityPerformance = 'Security Performance',
  AssetAllocation = 'Asset Allocation',
  InvestmentIncomeTax = 'Investment Income Tax Component Report',
  UXChangeTaxStatement = 'uXchange Tax Statement',
  CurrencyExposure = 'Currency Exposure',
  ProjectedIncomeCashflow = 'Projected Income Cashflow',
  MergeReport = 'Report',
  ConsolidatedDisclaimer = 'Consolidated Disclaimer',
}

export class MdaReportTypes extends Enumeration {
  static PortfolioValuation = new MdaReportTypes(1, 'Portfolio Valuation', MdaReportType.PortfolioValuation);
  static TransactionHistory = new MdaReportTypes(2, 'Transaction History', MdaReportType.TransactionHistory);
  static CashTransactions = new MdaReportTypes(3, 'Cash Transactions', MdaReportType.CashTransactions);
  static IncomeReport = new MdaReportTypes(4, 'Income Report', MdaReportType.IncomeReport);
  static RealisedCapitalGainsAndLosses = new MdaReportTypes(5, 'RealisedCapitalGainsAndLosses', MdaReportType.RealisedCapitalGainsAndLosses);
  static UnrealisedCapitalGainsAndLosses = new MdaReportTypes(6, 'UnrealisedCapitalGainsAndLosses', MdaReportType.UnrealisedCapitalGainsAndLosses);
  static Expenses = new MdaReportTypes(7, 'Expenses', MdaReportType.Expenses);
  static SecurityPerformance = new MdaReportTypes(8, 'Security Performance', MdaReportType.SecurityPerformance);
  static AssetAllocation = new MdaReportTypes(9, 'Asset Allocation', MdaReportType.AssetAllocation);
  static ContributionsAndWithdrawals = new MdaReportTypes(10, 'ContributionsAndWithdrawals', MdaReportType.ContributionsAndWithdrawals);
  static AssetClassPerformance = new MdaReportTypes(11, 'AssetClassPerformance', MdaReportType.AssetClassPerformance);
  static HoldingsVsTargetVariance = new MdaReportTypes(12, 'HoldingsVsTargetVariance', MdaReportType.HoldingsVsTargetVariance);
  static SecurityContribution = new MdaReportTypes(13, 'SecurityContribution', MdaReportType.SecurityContribution);
  static TaxSummary = new MdaReportTypes(14, 'TaxSummary', MdaReportType.TaxSummary);
  static TaxInvoice = new MdaReportTypes(15, 'TaxInvoice', MdaReportType.TaxInvoice);
  static PortfolioSnapshot = new MdaReportTypes(16, 'PortfolioSnapshot', MdaReportType.PortfolioSnapshot);
  static PortfolioPerformance = new MdaReportTypes(17, 'PortfolioPerformance', MdaReportType.PortfolioPerformance);
  static UnrealisedNonCgt = new MdaReportTypes(18, 'UnrealisedNonCgt', MdaReportType.UnrealisedNonCGT);
  static InvestmentIncomeTax = new MdaReportTypes(19, 'InvestmentIncomeTax', MdaReportType.InvestmentIncomeTax);
  static UXChangeTaxStatement = new MdaReportTypes(20, 'UXChangeTaxStatement', MdaReportType.UXChangeTaxStatement);
  static CurrencyExposure = new MdaReportTypes(22, 'CurrencyExposure', MdaReportType.CurrencyExposure);
  static ProjectedIncomeCashflow = new MdaReportTypes(23, 'ProjectedIncomeCashflow', MdaReportType.ProjectedIncomeCashflow);
  static MergeReport = new MdaReportTypes(100, 'Report', MdaReportType.MergeReport);
  static ConsolidatedDisclaimer = new MdaReportTypes(1000, 'Consolidated Disclaimer', MdaReportType.ConsolidatedDisclaimer);
}

export enum YearType {
  FinancialYear = 'Financial Year',
  CalendarYear = 'Calendar Year',
}

export type ReportComponentConfig =
  | ReportingComponentProps<AssetAllocationProps>
  | ReportingComponentProps<AssetClassPerformanceProps>
  | ReportingComponentProps<CashTransactionsProps>
  | ReportingComponentProps<ContributionsAndWithdrawalsProps>
  | ReportingComponentProps<CurrencyExposureProps>
  | ReportingComponentProps<ExpensesProps>
  | ReportingComponentProps<HoldingsVsTargetVarianceProps>
  | ReportingComponentProps<IncomeReportProps>
  | ReportingComponentProps<InvestmentIncomeTaxProps>
  | ReportingComponentProps<PorfolioPerformanceProps>
  | ReportingComponentProps<PortfolioSnapshotProps>
  | ReportingComponentProps<PortfolioValuationProps>
  | ReportingComponentProps<ProjectedIncomeCashflowProps>
  | ReportingComponentProps<RealisedCapitalGainsAndLossesProps>
  | ReportingComponentProps<SecurityContributionProps>
  | ReportingComponentProps<SecurityPerformanceProps>
  | ReportingComponentProps<TaxInvoiceProps>
  | ReportingComponentProps<TaxSummaryProps>
  | ReportingComponentProps<TransactionHistoryProps>
  | ReportingComponentProps<UnrealisedCapitalGainsAndLossesProps>
  | ReportingComponentProps<UnrealisedNonCGTGainsAndLossesProps>
  | ReportingComponentProps<UXChangeTaxStatementReportProps>;

export interface BaseReportComponentProps {
  parameters: ClientReportsParameters;
  setGroupConsolidatedOption?: ActionCreatorWithPayload<UpdateGroupConsolidatedOption>;
  disablePortfolioSelect?: boolean;
  createReportSortRequest: (isAdd: boolean, id: number, text: string) => void;
  isDatesLessThanFullYear?: (dates: DateRange) => boolean;
}

export interface ReportingComponentProps<T extends BaseReportComponentProps> {
  component: FC<T>;
  supportedFormats?: ReportFormat[];
  nonSuperOnly?: boolean;
  featureFlagged?: boolean;
  excelFeatureFlagged?: boolean;
  isAdminOnly?: boolean;
  disablePortfolioSelect?: boolean;
}

export interface CGTRecord {
  fyDescription: string;
  fyStart?: Date;
  fyEnd?: Date;
  strategyDescription: string;
  statusDescription: string;
  currentYear: boolean;
  finalisedDate?: Date;
  platformCarryForwardLoss?: number;
  externalCarryForwardLoss?: number;
}

export interface GetReportParameterListRequest {
  afslId: number[];
  kind: string;
}

export type SelectedReport<T = unknown> = Omit<T, 'SelectedReport'> & { expanded?: boolean; selected?: boolean };

export enum ReportFormat {
  PDF = 'PDF',
  Excel = 'Excel',
  JSON = 'Json',
}

export enum GroupConsolidatedOption {
  Consolidated = 'Consolidated',
  UnderlyingClients = 'UnderlyingClients',
  Both = 'Both',
}

export type GroupConsolidatedReport = {
  groupOption?: GroupConsolidatedOption;
};

export type DateRangeBaseRequest = {
  dateRangeType?: string | null;
  fromDate: string;
  toDate: string;
};

export type AsAtDateBaseRequest = {
  asAtDateType?: string | null;
  asAtDate: string;
};

export interface AssetAllocation extends AsAtDateBaseRequest, GroupConsolidatedReport {
  portfolioId: number;
  includeAssetCategory: boolean;
  includeDetailTable: boolean;
  applyExclusionList: boolean;
}

export interface ProjectedIncomeCashflow extends AsAtDateBaseRequest, GroupConsolidatedReport {
  portfolioId: number;
}

export interface AssetClassPerformance extends DateRangeBaseRequest, GroupConsolidatedReport {
  multiPeriods: PeriodBaseRequest[];
  excludeFrankingCredits: boolean;
  showAnnualisedReturns: boolean;
  applyExclusionList: boolean;
  excludeCapitalIncomeReturns: boolean;
  includeAllocationChange: boolean;
  includeReturnsSummary: boolean;
  includeBenchmarkComparison: boolean;
  performanceMethodology: PerformanceMethodology;
}

export interface HoldingsVsTargetVariance extends AsAtDateBaseRequest {
  applyExclusionList: boolean;
  includeSummary: boolean;
  portfolioId: number;
}

export interface CashTransactions extends DateRangeBaseRequest, GroupConsolidatedReport {
  sortBy: SortByCashTransactions;
  excludeAudValue: boolean;
}

export interface CurrencyExposure extends AsAtDateBaseRequest, GroupConsolidatedReport {
  portfolioId: number;
  includeDetailTable: boolean;
  applyExclusionList: boolean;
}

export interface Expenses extends DateRangeBaseRequest, GroupConsolidatedReport {
  excludeExpenseType: boolean;
  dateType: DateTypeExpensesReport;
}

export interface InvestmentIncomeTax extends DateRangeBaseRequest {
  excludeFxGainsLosses: boolean;
  yearType: YearType;
  version: string;
}

export interface RealisedCapitalGainsAndLosses extends DateRangeBaseRequest {
  includeSummary: boolean;
  excludeFxGainsLosses: boolean;
  showCostBaseAdjustments: boolean;
}

export interface UnrealisedCapitalGainsAndLosses extends AsAtDateBaseRequest {
  excludeFxGainsLosses: boolean;
  showCostBaseAdjustments: boolean;
}

export interface UnrealisedNonCGTGainsAndLosses extends AsAtDateBaseRequest {
  excludeFxGainsLosses: boolean;
}

export interface IncomeReport extends DateRangeBaseRequest, GroupConsolidatedReport {
  groupBy: GroupByIncomeReport;
  sortBy: SortByIncomeReport;
  dateType: DateTypeIncomeReport;
  showOutstandingPaymentOnly: boolean;
  showReceivablesAs: ShowReceivablesAsType;
  excludeCash: boolean;
}

export interface PortfolioPerformance extends DateRangeBaseRequest, GroupConsolidatedReport {
  multiPeriods: PeriodBaseRequest[];
  benchmarkId: number;
  excludeFrankingCredits: boolean;
  showAnnualisedReturns: boolean;
  applyExclusionList: boolean;
  includeAfterFeesReturn: boolean;
  includeNetContributions: boolean;
  primaryBenchmark: boolean;
  secondaryBenchmark: boolean;
  performanceMethodology: PerformanceMethodology;
}

export interface PortfolioSnapshot extends DateRangeBaseRequest, GroupConsolidatedReport {
  multiPeriods: PeriodBaseRequest[];
  benchmarkId: number;
  portfolioId: number;
  includeAfterFeesReturn: boolean;
  excludeFrankingCredits: boolean;
  performanceDataType: PerformanceDataType;
  performanceMethodology: PerformanceMethodology;
  primaryBenchmark: boolean;
  secondaryBenchmark: boolean;
  applyExclusionList: boolean;
}

export interface PortfolioValuation extends AsAtDateBaseRequest, GroupConsolidatedReport {
  excludeCostBase: boolean;
  excludeMarket: boolean;
  excludeSecurityType: boolean;
  excludeGICS: boolean;
  excludeGainsLosses: boolean;
  excludeAvgUnitCost: boolean;
  excludeEstIncomeYield: boolean;
  excludeEstGrossIncomeYield: boolean;
  excludeFXGainsLosses: boolean;
  excludeLocalPriceValue: boolean;
  excludePendingCashDetails: boolean;
  excludePriceDate: boolean;
  applyExclusionList: boolean;
  portfolioId: number;
  excludeInterestAccrual: boolean;
  excludeTotalReturn: boolean;
  groupBy: GroupByPortfolioValuation;
  sortBy: SortByPortfolioValuation;
}

export interface SecurityContribution extends DateRangeBaseRequest, GroupConsolidatedReport {
  groupBy: GroupBySecurityContribution;
  excludeFrankingCredits: boolean;
  showAnnualisedReturns: boolean;
  applyExclusionList: boolean;
  excludeCapitalIncomeReturns: boolean;
  includeDetailTable: boolean;
  performanceMethodology: PerformanceMethodology;
}

export interface SecurityPerformance extends DateRangeBaseRequest, GroupConsolidatedReport {
  excludeFrankingCredits: boolean;
  showAnnualisedReturns: boolean;
  applyExclusionList: boolean;
  excludeCapitalIncomeReturns: boolean;
  excludeMarketFxReturn: boolean;
  groupBy: GroupBySecurityPerformance;
  performanceMethodology: PerformanceMethodology;
}

export interface TransactionHistory extends DateRangeBaseRequest, GroupConsolidatedReport {
  groupBy: GroupByTransactionHistory;
  sortBy: SortByTransactionHistory;
  excludeCash: boolean;
  includeDistribution: boolean;
  excludeAssetDescription: boolean;
  excludeAccounts: boolean;
  includeBrokerage: boolean;
  includeGST: boolean;
  includeOtherCharges: boolean;
}

export interface SaveReportingParametersKindRequest {
  kind?: string;
  afslId?: number;
  request: SaveReportingParametersRequest;
}

export interface SaveReportingParametersRequest extends InitiateReportRequest {
  visibleToAfsl: boolean;
  parameterName?: string;
}

export interface InitiateReportRequestModel {
  id: string;
  downloadFileName: string;
}

export interface TaxSummary extends DateRangeBaseRequest {
  yearType: YearType;
}

export interface InitiateReportRequest extends DateRangeBaseRequest {
  clientId: number;
  includeLogo: boolean;
  mergeReportOrder: number[];
  contributionsAndWithdrawals?: ContributionsAndWithdrawals;
  assetAllocation?: AssetAllocation;
  assetClassPerformance?: AssetClassPerformance;
  projectedIncomeCashflow?: ProjectedIncomeCashflow;
  holdingsVsTargetVariance?: HoldingsVsTargetVariance;
  cashTransactions?: CashTransactions;
  expenses?: Expenses;
  incomeReport?: IncomeReport;
  currencyExposure?: CurrencyExposure;
  securityPerformance?: SecurityPerformance;
  portfolioSnapshot?: PortfolioSnapshot;
  portfolioPerformance?: PortfolioPerformance;
  portfolioValuation?: PortfolioValuation;
  securityContribution?: SecurityContribution;
  taxSummary?: TaxSummary;
  taxInvoice?: DateRangeBaseRequest;
  investmentIncomeTaxComponent?: InvestmentIncomeTax;
  transactionHistory?: TransactionHistory;
  realisedCapitalGainsAndLosses?: RealisedCapitalGainsAndLosses;
  unrealisedCapitalGainsAndLosses?: UnrealisedCapitalGainsAndLosses;
  unrealisedNonCGT?: UnrealisedNonCGTGainsAndLosses;
  uXchangeTaxStatement?: DateRangeBaseRequest;
  includeCoverLetter: boolean;
  includeAppendix: boolean;
  isDataFromTriumph: boolean;
  coverLetter?: string;
  appendix?: string;
  reportFormat?: string;
  parameterName?: string;
}

export interface FetchClientReport {
  key: string;
  clientId?: number;
  downloadFileName: string;
  extension?: boolean;
}

export interface ReportGeneration {
  isGenerating: boolean;
  requestId: string;
  downloadFileName: string;
  isExpired: boolean;
  key: string;
  isDataFromTriumph: boolean;
  isMergeReport: boolean;
  clientId: number;
  clientName: string;
  isGenerated: boolean;
  mergeReportOrder: number[];
  dateRange: string;
  reportName: string;
  status: string;
  statusMessage: string;
}

export interface ReportParameterTemplateViewModel extends InitiateReportRequest {
  id: string;
  afslId?: number;
  afslName?: string;
  createdById: number;
  createdByName?: string;
  createdOn: Date;
  modifiedOn?: Date;
  visibleToAfsl: boolean;
  parameterName?: string;
  kind?: string;
}

export interface TempFileRef {
  fileRef: string;
}

export enum PerformanceMethodology {
  TimeWeighted = 'TimeWeighted_DerivedIncome', // default
  ReturnOnAverage = 'ReturnOnAverage_DerivedIncome',
}

export enum HideByAssetClassPerformance {
  FrankingCredits = 'excludeFrankingCredits',
  AnnualisedReturns = 'showAnnualisedReturns',
  ExclusionList = 'applyExclusionList',
  CapitalIncomeReturns = 'excludeCapitalIncomeReturns',
  AllocationChange = 'includeAllocationChange',
  ReturnsSummary = 'includeReturnsSummary',
  BenchmarkComparison = 'includeBenchmarkComparison',
}

export enum HideByTransactionHistory {
  Cash = 'excludeCash',
  Distribution = 'includeDistribution',
  AssetDescription = 'excludeAssetDescription',
  Accounts = 'excludeAccounts',
  Brokerage = 'includeBrokerage',
  Gst = 'includeGST',
  OtherCharges = 'includeOtherCharges',
}

export enum SortByTransactionHistory {
  Account = 'Account',
  TradeDate = 'TradeDate',
  SecurityName = 'SecurityName',
  TransactionType = 'TransactionType',
}

export enum GroupByTransactionHistory {
  Account = 'Account',
  TradeDate = 'TradeDate',
  SecurityName = 'SecurityName',
  TransactionType = 'TransactionType',
}

export enum HidingOptionSecurityPerformance {
  FrankingCredits = 'excludeFrankingCredits',
  AnnualisedReturns = 'showAnnualisedReturns',
  ExclusionList = 'applyExclusionList',
  CapitalIncomeReturn = 'excludeCapitalIncomeReturns',
  MarketFxReturn = 'excludeMarketFxReturn',
}

export enum HidingOptionPortfolioPerformance {
  FrankingCredits = 'excludeFrankingCredits',
  AnnualisedReturns = 'showAnnualisedReturns',
  ExclusionList = 'applyExclusionList',
  AfterFeesReturn = 'includeAfterFeesReturn',
  NetContributions = 'includeNetContributions',
  PrimaryBenchmark = 'primaryBenchmark',
  SecondaryBenchmark = 'secondaryBenchmark',
}

export enum FilterByTransactions {
  excludeCash = 'ExcludeCash',
  includeDistribution = 'IncludeDistribution',
}

export enum GroupBySecurityPerformance {
  AssetClass = 'AssetClass',
  Gics = 'GICS',
}

export enum GroupByIncomeReport {
  Account = 'Account',
  IncomeType = 'IncomeType',
}

export enum SortByCashTransactions {
  DateAscending = 'Ascending',
  DateDescending = 'Descending',
}

export enum DateTypeIncomeReport {
  ExDate = 'ExDate',
  TaxDate = 'TaxDate',
  PaymentDate = 'PaymentDate',
}

export enum ShowReceivablesAsType {
  UnmappedAccruals = 'UnmappedAccruals',
  PendingOnReportDate = 'PendingOnReportDate',
}

export enum SortByIncomeReport {
  Security = 'Security',
  PaymentDate = 'PaymentDate',
}

export enum DateTypeExpensesReport {
  TradeDate = 'TradeDate',
  TaxDate = 'TaxDate',
}

export enum HidingOptionHoldingsVsTargetVariance {
  ExclusionList = 'applyExclusionList',
  Summary = 'includeSummary',
}

export enum HidingOptionCurrencyExposure {
  IncludeDetailTable = 'includeDetailTable',
  ApplyExclusionList = 'applyExclusionList',
}

export interface HidingOptionsRequest<T> {
  option: T;
  value: boolean;
}

export enum HidingOptionAssetAllocation {
  AssetCategory = 'includeAssetCategory',
  DetailTable = 'includeDetailTable',
  ExclusionList = 'applyExclusionList',
}

export enum RealisedCapitalGainsAndLossesOption {
  IncludeSummary = 'includeSummary',
  ExcludeFxGainsLosses = 'excludeFxGainsLosses',
  ShowCostBaseAdjustments = 'showCostBaseAdjustments',
}

export enum HideByPortfolioValuation {
  SecurityType = 'excludeSecurityType',
  CostBase = 'excludeCostBase',
  Gics = 'excludeGICS',
  Market = 'excludeMarket',
  GainsLosses = 'excludeGainsLosses',
  AvgUnitCost = 'excludeAvgUnitCost',
  EstIncomeAndYield = 'excludeEstIncomeYield',
  EstGrossIncomeAndYield = 'excludeEstGrossIncomeYield',
  FxGainsLosses = 'excludeFXGainsLosses',
  LocalPriceAndValue = 'excludeLocalPriceValue',
  ExcludeInterestAccrual = 'excludeInterestAccrual',
  ExcludePendingCashDetails = 'excludePendingCashDetails',
  ExclusionList = 'applyExclusionList',
  ExcludePriceDate = 'excludePriceDate',
  ExcludeTotalReturn = 'excludeTotalReturn',
}

export enum HideBySecurityContribution {
  FrankingCredit = 'excludeFrankingCredits',
  AnnualisedReturn = 'showAnnualisedReturns',
  ExclusionList = 'applyExclusionList',
  CapitalIncomeReturn = 'excludeCapitalIncomeReturns',
  DetailTable = 'includeDetailTable',
}

export enum SortByPortfolioValuation {
  SecurityType = 'SecurityType',
  SecurityName = 'SecurityName',
  Gics = 'GICS',
  Model = 'Model',
  Portfolio = 'Portfolio',
  AssetClass = 'AssetClass',
  ValueDescend = 'ValueDescend',
  ValueAscend = 'ValueAscend',
}

export enum GroupBySecurityContribution {
  AssetClass = 'AssetClass',
  Gics = 'GICS',
}

export enum GroupByPortfolioValuation {
  AssetClass = 'AssetClass',
  Gics = 'GICS',
  Portfolio = 'Portfolio',
  Model = 'Model',
}

export enum PerformanceDataType {
  Daily = 'Daily',
  Monthly = 'Monthly',
}

export interface SelectedReports {
  incomeReport: SelectedReport<IncomeReport>;
  assetClassPerformance: SelectedReport<AssetClassPerformance>;
  cashTransactions: SelectedReport<CashTransactions>;
  expenses: SelectedReport<Expenses>;
  currencyExposure: SelectedReport<CurrencyExposure>;
  projectedIncomeCashflow: SelectedReport<ProjectedIncomeCashflow>;
  unrealisedCapitalGainsAndLosses: SelectedReport<UnrealisedCapitalGainsAndLosses>;
  realisedCapitalGainsAndLosses: SelectedReport<RealisedCapitalGainsAndLosses>;
  securityPerformance: SelectedReport<SecurityPerformance>;
  portfolioValuation: SelectedReport<PortfolioValuation>;
  assetAllocation: SelectedReport<AssetAllocation>;
  contributionsAndWithdrawals: SelectedReport<ContributionsAndWithdrawals>;
  holdingsVsTargetVariance: SelectedReport<HoldingsVsTargetVariance>;
  portfolioSnapshot: SelectedReport<PortfolioSnapshot>;
  taxInvoice: SelectedReport<DateRangeBaseRequest>;
  portfolioPerformance: SelectedReport<PortfolioPerformance>;
  securityContribution: SelectedReport<SecurityContribution>;
  taxSummary: SelectedReport<TaxSummary>;
  investmentIncomeTaxComponent: SelectedReport<InvestmentIncomeTax>;
  unrealisedNonCGT: SelectedReport<UnrealisedNonCGTGainsAndLosses>;
  transactionHistory: SelectedReport<TransactionHistory>;
  uXchangeTaxStatement: SelectedReport<DateRangeBaseRequest>;
}

export interface ContributionsAndWithdrawals extends DateRangeBaseRequest, GroupConsolidatedReport {}

export interface ClientReportsParameters {
  clientName: string;
  clientSubTypeId: number;
  inceptionDate: string;
  showItemOrdering: boolean;
  dates: DateRange;
  isDataFromTriumph: boolean;
  mergeReports: boolean;
  includeLogo: boolean;
  reportingParameter: ReportingParameter;
  reportsToSort: ReportToSort[];
  expandedReports: boolean;
  selectedReports: SelectedReports;
  selectedParameter?: SavedParameter | null;
  includeCoverLetter: boolean;
  includeAppendix: boolean;
  coverLetter?: string;
  appendix?: string;
  reportFormat?: string;
  parameterName?: string;
}

export interface UpdateGroupConsolidatedOption {
  report: MdaReportTypeField;
  option: GroupConsolidatedOption;
}

export interface ReportingParameter {
  visibleToAfsl: boolean;
  parameterName: string;
  afslId?: number;
  afslName?: string;
  kind?: string;
}

export interface CreateInitiateReportRequest {
  isDataFromTriumph: boolean;
  clientId?: number;
  parameters: ClientReportsParameters;
  handleClickMergeReports: () => void;
}

export interface ReportResult {
  data: [];
}

export interface ReportToSort {
  id: number;
  text: string;
}

export enum ReportAttachmentType {
  None = 'None',
  CoverLetter = 'Cover Letter',
  Appendix = 'Appendix',
}

export interface SavedParameter {
  id: string;
  name: string;
}

export interface FetchReportParametersRequest {
  id: string;
}

export interface PeriodBaseRequest {
  periodType: string;
  periodValue?: number;
}

export class PeriodType extends Enumeration {
  static Month = new MdaReportTypes(1, 'Month', 'Month');
  static Year = new MdaReportTypes(2, 'Year', 'Year');
  static FinancialYear = new MdaReportTypes(3, 'FinancialYear', 'Financial Year');
  static CalendarYear = new MdaReportTypes(4, 'CalendarYear', 'Calendar Year');
  static SinceInception = new MdaReportTypes(5, 'SinceInception', 'Since Inception');
}

export interface AvailablePeriod {
  displayName: string;
  periodType: PeriodType;
  periodValue?: number;
}
